import compact from "lodash/compact"
import fromPairs from "lodash/fromPairs"
import includes from "lodash/includes"
import isEqual from "lodash/isEqual"
import omit from "lodash/omit"
import uniq from "lodash/uniq"

const emptyStep = {
  channel_module_name: null,
  module_name: null,
  full_module_name: null,
  fields: [],
}

const initialState = {
  // Track whether we're currently waiting for applet data from the server (or
  // for the client to initialize itself).
  isLoading: true,

  // Track whether we're currently submitting the applet to the server.
  isSending: false,

  // Track whether this applet belongs to a Maker user, as opposed to a partner
  // service. This matters because partner applets don't have a `channel_id`.
  isMaker: null,

  // In order to maintain the correctness of the `channel_id` field, we need to
  // know the ids of the current trigger and action channels. This requires us
  // to denormalize a bit in order to avoid worse contortions, since we only
  // reference services by module name in the applet's actual data.
  serviceIdsByModuleName: {},

  // The remaining fields are all parameters expected/returned by the server.
  id: null,
  name: "",
  description: "",
  published: false,
  archived: false,
  filter_code: null,
  channel_id: null,
  applet_trigger: emptyStep,
  applet_queries: [],
  applet_actions: [],
  tags: [],
}

const emptyField = {
  name: null,
  custom_label: "",
  hidden: false,
  default_value: null,
}

export default function applet(state = initialState, event) {
  switch (event.type) {
    default: {
      return state
    }

    case "SERVICES_LOADED": {
      return {
        ...state,
        serviceIdsByModuleName: {
          ...state.serviceIdsByModuleName,
          ...fromPairs(event.services.map(s => [s.module_name, s.id])),
        },
      }
    }

    case "NEW_APPLET": {
      return { ...state, isLoading: false, isMaker: event.isMaker }
    }

    case "APPLET_LOADED": {
      // We can mostly use the GraphQL data as-is, but we do have to parse
      // `default_value_json` into `default_value` so we can work with it.
      return {
        ...initialState,
        ...mapAppletFields(event.applet, field => ({
          ...omit(field, "default_value_json"),
          default_value: field.default_value === undefined ? JSON.parse(field.default_value_json) : field.default_value,
        })),
        isLoading: false,
        isMaker: event.isMaker,
        serviceIdsByModuleName: state.serviceIdsByModuleName,
        channel_id: event.isMaker ? event.applet.channel_id : null,
        tags: event.applet.tags,
      }
    }

    case "UPDATE_APPLET_METADATA": {
      return { ...state, ...event.data }
    }

    case "SELECT_TRIGGER_SERVICE": {
      return ensureValidChannelId({
        ...state,
        applet_trigger: {
          ...emptyStep,
          channel_module_name: event.service.module_name,
        },
      })
    }

    case "SELECT_TRIGGER": {
      const fields = event.trigger.fields.map(tf => ({
        ...emptyField,
        name: tf.name,
        custom_label: tf.label,
        hidden: tf.hideable,
      }))

      return {
        ...state,
        applet_trigger: {
          channel_module_name: event.trigger.channel_module_name,
          module_name: event.trigger.module_name,
          fields,
        },
      }
    }

    case "REMOVE_TRIGGER": {
      return ensureValidChannelId({
        ...state,
        applet_trigger: emptyStep,
      })
    }

    case "UPDATE_TRIGGER_FIELD": {
      const fieldIndex = state.applet_trigger.fields.findIndex(f => f.name === event.field.name)

      const field = {
        ...state.applet_trigger.fields[fieldIndex],
        ...event.data,
      }

      const fields = state.applet_trigger.fields.slice()
      fields.splice(fieldIndex, 1, field)

      return {
        ...state,
        applet_trigger: {
          ...state.applet_trigger,
          fields,
        },
      }
    }

    case "ADD_ACTION": {
      return {
        ...state,
        applet_actions: state.applet_actions.concat([emptyStep]),
      }
    }

    case "ADD_QUERY": {
      return {
        ...state,
        applet_queries: state.applet_queries.concat([emptyStep]),
      }
    }

    case "REMOVE_ACTION": {
      const applet_actions = state.applet_actions.slice()
      applet_actions.splice(event.index, 1)

      return ensureValidChannelId({ ...state, applet_actions })
    }

    case "REMOVE_QUERY": {
      const applet_queries = state.applet_queries.slice()
      applet_queries.splice(event.index, 1)

      return ensureValidChannelId({ ...state, applet_queries })
    }

    case "SELECT_ACTION_SERVICE": {
      const action = {
        ...emptyStep,
        channel_module_name: event.service.module_name,
      }

      const applet_actions = state.applet_actions.slice()
      applet_actions.splice(event.index, 1, action)

      return ensureValidChannelId({ ...state, applet_actions })
    }

    case "SELECT_QUERY_SERVICE": {
      const query = {
        ...emptyStep,
        channel_module_name: event.service.module_name,
      }

      const applet_queries = state.applet_queries.slice()
      applet_queries.splice(event.index, 1, query)

      return ensureValidChannelId({ ...state, applet_queries })
    }

    case "SELECT_ACTION": {
      const fields = event.action.fields.map(af => ({
        ...emptyField,
        name: af.name,
        custom_label: af.label,
        default_value: event.defaults[af.name],
        hidden: af.hideable,
      }))

      const action = {
        channel_module_name: event.action.channel_module_name,
        module_name: event.action.module_name,
        fields,
      }

      const applet_actions = state.applet_actions.slice()
      applet_actions.splice(event.index, 1, action)

      return { ...state, applet_actions }
    }

    case "SELECT_QUERY": {
      const fields = event.query.fields.map(qf => ({
        ...emptyField,
        name: qf.name,
        custom_label: qf.label,
        hidden: qf.hideable,
      }))

      const query = {
        channel_module_name: event.query.channel_module_name,
        module_name: event.query.module_name,
        fields,
      }

      const applet_queries = state.applet_queries.slice()
      applet_queries.splice(event.index, 1, query)

      return { ...state, applet_queries }
    }

    case "UPDATE_ACTION_FIELD": {
      const action = state.applet_actions[event.index]
      const fieldIndex = action.fields.findIndex(f => f.name === event.field.name)

      const field = { ...action.fields[fieldIndex], ...event.data }

      const fields = action.fields.slice()
      fields.splice(fieldIndex, 1, field)

      const newAction = { ...action, fields }

      const applet_actions = state.applet_actions.slice()
      applet_actions.splice(event.index, 1, newAction)

      return { ...state, applet_actions }
    }

    case "UPDATE_QUERY_FIELD": {
      const query = state.applet_queries[event.index]
      const fieldIndex = query.fields.findIndex(f => f.name === event.field.name)

      const field = { ...query.fields[fieldIndex], ...event.data }

      const fields = query.fields.slice()
      fields.splice(fieldIndex, 1, field)

      const newquery = { ...query, fields }

      const applet_queries = state.applet_queries.slice()
      applet_queries.splice(event.index, 1, newquery)

      return { ...state, applet_queries }
    }

    case "INVALID_FIELD_RESOLUTION": {
      // Process "/triggers/foo.bar" into ["triggers", "foo", "bar"]
      const fieldSpec = event.field.stored_field.owner.split(/[/.]/).slice(1)

      return mapAppletFields(state, (appletField, serviceName, tandaName, type) => {
        if (isEqual(fieldSpec, [type, serviceName, tandaName])) {
          return { ...appletField, hidden: true }
        } else {
          return appletField
        }
      })
    }

    case "CREATE_OR_UPDATE_APPLET": {
      return { ...state, isSending: true }
    }

    case "APPLET_CREATE_OR_UPDATE_FAILED": {
      return { ...state, isSending: false }
    }
  }
}

export const appletQueryFields = `
  id
  name
  description
  published
  archived
  filter_code
  channel_id
  tags { id name }

  applet_trigger {
    channel_module_name
    module_name
    trigger { full_module_name }

    fields {
      name
      custom_label
      hidden
      default_value_json
    }
  }

  applet_actions {
    channel_module_name
    module_name
    action { full_module_name }

    fields {
      name
      custom_label
      hidden
      default_value_json
    }
  }

  applet_queries {
    channel_module_name
    module_name
    query { full_module_name }

    fields {
      name
      custom_label
      hidden
      default_value_json
    }
  }
`

/**
 * Strip client-side-only fields to prepare an applet for persistence or
 * submission to the API. Returns a tuple of id (or null for new applets) and
 * an object.
 */
export function stripExtraFields(applet) {
  const shallowCopy = Object.assign({}, applet)
  const id = applet.id

  delete shallowCopy.id
  delete shallowCopy.isLoading
  delete shallowCopy.isSending
  delete shallowCopy.isMaker
  delete shallowCopy.serviceIdsByModuleName

  return [id, shallowCopy]
}

/**
 * Map all applet trigger fields and applet action fields through the given
 * function.
 */
function mapAppletFields(applet, fn) {
  return {
    ...applet,
    applet_trigger: {
      ...applet.applet_trigger,
      full_module_name: applet.applet_trigger?.trigger?.full_module_name,
      fields: applet.applet_trigger.fields.map(field => {
        const { channel_module_name, module_name } = applet.applet_trigger
        return fn(field, module_name, channel_module_name, "triggers")
      }),
    },
    applet_actions: (applet.applet_actions || []).map(appletAction => ({
      ...appletAction,
      full_module_name: appletAction.action?.full_module_name,
      fields: appletAction.fields.map(field => {
        const { channel_module_name, module_name } = appletAction
        return fn(field, module_name, channel_module_name, "actions")
      }),
    })),
    applet_queries: (applet.applet_queries || []).map(appletQuery => ({
      ...appletQuery,
      full_module_name: appletQuery.query.full_module_name,
      fields: appletQuery.fields.map(field => {
        const { channel_module_name, module_name } = appletQuery
        return fn(field, module_name, channel_module_name, "queries")
      }),
    })),
  }
}

/**
 * Given a pending applet state, make sure the `channel_id` field has a sane
 * value. If it's already set to the id of a channel that is associated with
 * the applet, keep the setting; otherwise, default to the trigger's channel if
 * there is one; otherwise, set it to null.
 *
 * NOTE: This should only be called once the services have been loaded; it's
 * safe to do it for any action that can only happen in response to the user
 * doing something, but not for APPLET_LOADED, since that can get called too
 * early.
 */
function ensureValidChannelId(applet) {
  // Non-maker applets must have a `null` value for `channel_id`.
  if (!applet.isMaker) {
    return { ...applet, channel_id: null }
  }

  const triggerChannelId =
    applet.applet_trigger.channel_module_name &&
    applet.serviceIdsByModuleName[applet.applet_trigger.channel_module_name]

  const channelIds = compact(
    uniq([
      triggerChannelId,
      ...applet.applet_actions.map(aa => applet.serviceIdsByModuleName[aa.channel_module_name]),
      ...applet.applet_queries.map(aq => applet.serviceIdsByModuleName[aq.channel_module_name]),
    ])
  )

  if (includes(channelIds, applet.channel_id)) {
    return applet
  }

  if (triggerChannelId) {
    return { ...applet, channel_id: triggerChannelId }
  }

  return { ...applet, channel_id: null }
}
